import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/page-header";
import { PartnerFormModal } from "@/components/partner-form-modal";
import { apiRequest } from "@/lib/queryClient";
import type { Partner } from "@shared/schema";
import { Link } from "wouter";

export default function PartnerList() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [partnerTypeFilter, setPartnerTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data, isLoading } = useQuery<Partner[]>({
    queryKey: ["/api/crm/partners", searchTerm, partnerTypeFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (partnerTypeFilter !== "all") params.append("partnerType", partnerTypeFilter);
      if (statusFilter !== "all") params.append("isActive", statusFilter);

      const response = await apiRequest("GET", `/api/crm/partners?${params.toString()}`);
      return response.json();
    },
  });

  const partners = Array.isArray(data) ? data : [];

  const partnerTypes = [
    { value: "all", label: "All Types" },
    { value: "mortgage_broker", label: "Mortgage Broker" },
    { value: "accountant", label: "Accountant" },
    { value: "financial_planner", label: "Financial Planner" },
    { value: "lawyer", label: "Lawyer" },
    { value: "real_estate_agent", label: "Real Estate Agent" },
    { value: "business_broker", label: "Business Broker" },
    { value: "other", label: "Other" },
  ];

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ];

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Referral Partners"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Partners" }]}
        actions={
          <Button onClick={() => setIsModalOpen(true)} data-testid="button-add-partner">
            <Plus className="mr-2 h-4 w-4" />
            Add Partner
          </Button>
        }
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-4">
        <div className="mx-auto max-w-7xl space-y-4">
          {/* Search and Filters */}
          <Card className="p-3">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search partners..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="input-search-partners"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear search"
                    data-testid="button-clear-search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex gap-2">
                <Select value={partnerTypeFilter} onValueChange={setPartnerTypeFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Partner Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {partnerTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {/* Partners Table */}
          <Card>
            <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Partner Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Commission Rate</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">
                  Loading partners...
                </TableCell>
              </TableRow>
            ) : partners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">
                  No partners found
                </TableCell>
              </TableRow>
            ) : (
              partners.map((partner) => (
                <TableRow key={partner.id}>
                  <TableCell>
                    <Link href={`/partners/${partner.id}`} className="font-medium hover:underline">
                      {partner.partnerName}
                    </Link>
                    {partner.companyName && (
                      <div className="text-sm text-muted-foreground">{partner.companyName}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    {partner.partnerType ? (
                      <Badge variant="outline">
                        {partnerTypes.find((t) => t.value === partner.partnerType)?.label || partner.partnerType}
                      </Badge>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {partner.email && <div>{partner.email}</div>}
                      {partner.phone && <div className="text-muted-foreground">{partner.phone}</div>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {partner.defaultCommissionRate ? `${partner.defaultCommissionRate}%` : "-"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={partner.isActive ? "default" : "secondary"}>
                      {partner.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Link href={`/partners/${partner.id}`}>
                      <Button variant="ghost" size="sm">
                        View
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  </main>

  <PartnerFormModal
    open={isModalOpen}
    onOpenChange={setIsModalOpen}
  />
</div>
  );
}
